import { useState } from 'react';
import './App.css';
import ThreeCanvas from './components/ThreeCanvas';
import UIOverlay from './components/UIOverlay';
import GenericModal from './components/GenericModal';

const App = () => {

  // ── Contact form state ──────────────────────────────────
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // ── Consultation form state ─────────────────────────────
  const [consultWho, setConsultWho] = useState('company');
  const [consultIndustry, setConsultIndustry] = useState('');
  const [consultName, setConsultName] = useState('');
  const [consultEmail, setConsultEmail] = useState('');
  const [consultNeeds, setConsultNeeds] = useState('');
  const [isConsultSubmitting, setIsConsultSubmitting] = useState(false);
  const [consultStatus, setConsultStatus] = useState(null);

  // ── UI / Modal state ────────────────────────────────────
  const [activeSection, setActiveSection] = useState(null); // 'projects' | 'about' | 'contact' | 'consultation'
  const [selectedProject, setSelectedProject] = useState(null);

  // ── Projects data ───────────────────────────────────────
  const projects = [
    {
      id: 1,
      title: "Prime Number hangman game",
      description: "A fun and educational game to learn about prime numbers",
      fullDescription: "Prime Number Hangman is an interactive game designed to help players learn about prime numbers in a fun and engaging way. Players guess numbers to reveal a hidden prime number, while also learning interesting facts about them. The game includes multiple levels of difficulty, hints, and educational content that makes it suitable for all ages, from students to adults interested in mathematics.",
      image: "./chat.png",
      link: "https://primes.protekar.eu"
    },
    {
      id: 2,
      title: "BinauralVibes",
      description: "An audio processing system for binaural beats and soundscapes",
      fullDescription: "BinauralVibes is an advanced audio processing system that creates immersive soundscapes using binaural audio. It analyzes audio inputs to generate unique sound patterns that promote relaxation, focus, and meditation. The system features customizable sound profiles, real-time audio manipulation, and integration with various audio sources, making it ideal for wellness applications, meditation practices, and therapeutic environments.",
      image: "./creatives_1.png",
      link: "https://binauralvibes.protekar.eu"
    },
    {
      id: 3,
      title: "stegaVibe",
      description: "A steganography processing system for secure data transmission",
      fullDescription: "A two way steganography encoding and decoding system that allows users to securely hide data in images. The system uses advanced algorithms to embed data within image files, ensuring that the hidden information remains undetectable to the naked eye. It supports various image formats and provides a user-friendly interface for both encoding and decoding processes. This solution is ideal for secure communications, data protection, and privacy-sensitive applications.",
      image: "./pipeline_2.png",
      link: "https://stegavibe.protekar.eu"
    },
    {
      id: 4,
      title: "SuperTrader",
      description: "A simple web-based trading game",
      fullDescription: "Supertrader is a simple web-based trading game simulation for the hardcore fanatics",
      image: "./kyoto.png",
      link: "https://supertrader.protekar.eu"
    },
    {
      id: 5,
      title: "Project Epsilon",
      description: "Customer relationship management and engagement platform",
      fullDescription: "Project Epsilon is a comprehensive customer relationship management platform that transforms customer engagement strategies. By analyzing customer interactions, behavior patterns, and feedback across multiple channels, we help businesses build stronger, more meaningful relationships with their customers. The system features customer analytics, engagement tracking, communication tools, and reporting dashboards that enable businesses to make data-driven decisions for enhanced customer satisfaction.",
      image: "./customer_rel.png",
      link: "#"
    },
    {
      id: 6,
      title: "Project Zeta",
      description: "Custom hardware integration and embedded systems solutions",
      fullDescription: "Project Zeta specializes in custom hardware integration and embedded systems development. We focus on creating optimized solutions that can run efficiently on various hardware platforms while maintaining high performance and reliability. The solution includes custom development, hardware-specific optimization, performance analysis, and seamless integration with existing systems, helping businesses implement advanced capabilities in real-world conditions.",
      image: "./scientists.png",
      link: "#"
    }
  ];

  // ── Contact form submit ─────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact / Protekar Website",
          name,
          email,
          message,
          source
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setMessage('');
        setSource('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Consultation form submit ────────────────────────────
  const handleConsultSubmit = async (e) => {
    e.preventDefault();
    setIsConsultSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          subject: "Consultation Request / Protekar Website",
          type: consultWho,
          industry: consultIndustry,
          name: consultName,
          email: consultEmail,
          needs: consultNeeds
        }),
      });

      const result = await response.json();
      if (result.success) {
        setConsultStatus('success');
        setConsultWho('company');
        setConsultIndustry('');
        setConsultName('');
        setConsultEmail('');
        setConsultNeeds('');
      } else {
        setConsultStatus('error');
      }
    } catch {
      setConsultStatus('error');
    } finally {
      setIsConsultSubmitting(false);
    }
  };

  const handleOpenProject = (project) => {
    setSelectedProject(project);
  };

  return (
    <div className="App">

      {/* Fixed 3-D background */}
      <ThreeCanvas />

      {/* Scrollable UI overlay */}
      <UIOverlay
        onOpenContact={() => setActiveSection('contact')}
        onOpenProjects={() => setActiveSection('projects')}
        onOpenAbout={() => setActiveSection('about')}
        onOpenConsultation={() => setActiveSection('consultation')}
      />

      {/* ── Projects Grid Modal ── */}
      <GenericModal
        isOpen={activeSection === 'projects'}
        onClose={() => setActiveSection(null)}
        title="Our Projects"
      >
        <div className="projects-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => handleOpenProject(project)}
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
              </div>
              <h3>{project.title}</h3>
              <p>{project.description}</p>
              <button className="btn learn-more">Details {'>>'}</button>
            </div>
          ))}
        </div>
      </GenericModal>

      {/* ── Project Detail Modal ── */}
      <GenericModal
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
        title={selectedProject?.title}
      >
        {selectedProject && (
          <>
            <div className="modal-image-container">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="modal-image"
              />
            </div>
            <p className="modal-description">{selectedProject.fullDescription}</p>
            <div className="modal-actions">
              <button
                className="modal-visit-btn"
                onClick={() => window.open(selectedProject.link, '_blank')}
              >
                Launch System
              </button>
            </div>
          </>
        )}
      </GenericModal>

      {/* ── About Modal ── */}
      <GenericModal
        isOpen={activeSection === 'about'}
        onClose={() => setActiveSection(null)}
        title="About Protekar"
      >
        <div className="about-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <p>
            Protekar is a technology company focused on delivering innovative solutions for businesses across industries.
            We combine technical expertise with deep industry knowledge to create custom solutions that drive
            real business value and operational efficiency.
          </p>
          <p>
            Our team of specialists and industry experts work collaboratively to understand
            your specific challenges and develop tailored solutions that integrate seamlessly with your existing
            systems and processes.
          </p>
          <div className="about-image" style={{ textAlign: 'center' }}>
            <img src="./creatives.png" alt="Protekar Team" style={{ maxWidth: '100%', border: '1px solid #444' }} />
          </div>
        </div>
      </GenericModal>

      {/* ── Contact Modal ── */}
      <GenericModal
        isOpen={activeSection === 'contact'}
        onClose={() => setActiveSection(null)}
        title="Contact Us"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              required
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
            />
          </div>
          <div className="form-group">
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              required
            >
              <option value="">How did you hear about us?</option>
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter</option>
              <option value="google">Google</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Message"
              required
              rows="5"
            />
          </div>
          <button
            type="submit"
            className="btn"
            disabled={isSubmitting}
            style={{ width: '100%' }}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          {submitStatus === 'success' && (
            <p style={{ color: '#00ADB5', marginTop: '15px', fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>
              Transmission successful. We'll be in touch.
            </p>
          )}
          {submitStatus === 'error' && (
            <p style={{ color: '#ff4444', marginTop: '15px', fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>
              Transmission failed. Please retry.
            </p>
          )}
        </form>
      </GenericModal>

      {/* ── Consultation Modal ── */}
      <GenericModal
        isOpen={activeSection === 'consultation'}
        onClose={() => { setActiveSection(null); setConsultStatus(null); }}
        title="Book a Consultation"
      >
        <form onSubmit={handleConsultSubmit}>

          {/* Who are you */}
          <label className="form-label">Who are you?</label>
          <div className="form-radio-group">
            <label className="form-radio-option">
              <input
                type="radio"
                name="consultWho"
                value="company"
                checked={consultWho === 'company'}
                onChange={(e) => setConsultWho(e.target.value)}
              />
              Company / Organisation
            </label>
            <label className="form-radio-option">
              <input
                type="radio"
                name="consultWho"
                value="individual"
                checked={consultWho === 'individual'}
                onChange={(e) => setConsultWho(e.target.value)}
              />
              Individual
            </label>
          </div>

          {/* Industry / Market */}
          <div className="form-group">
            <label className="form-label">Field / Market</label>
            <select
              value={consultIndustry}
              onChange={(e) => setConsultIndustry(e.target.value)}
              required
            >
              <option value="">Select your industry</option>
              <option value="manufacturing">Manufacturing & Industrial Automation</option>
              <option value="healthcare">Healthcare & Medical Devices</option>
              <option value="energy">Energy & Utilities</option>
              <option value="logistics">Logistics & Supply Chain</option>
              <option value="finance">Finance & Trading</option>
              <option value="defense">Defense & Aerospace</option>
              <option value="legal">Legal & Compliance</option>
              <option value="research">Research & Academia</option>
              <option value="retail">Retail & E-commerce</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Contact details */}
          <div className="form-group">
            <label className="form-label">Contact Details</label>
            <input
              type="text"
              value={consultName}
              onChange={(e) => setConsultName(e.target.value)}
              placeholder="Name"
              required
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              value={consultEmail}
              onChange={(e) => setConsultEmail(e.target.value)}
              placeholder="Email address"
              required
            />
          </div>

          {/* Further needs */}
          <div className="form-group">
            <label className="form-label">Tell us about your needs</label>
            <textarea
              value={consultNeeds}
              onChange={(e) => setConsultNeeds(e.target.value)}
              placeholder="Describe your current challenge, project scope, or what you would like to explore. A few lines is enough to get the conversation started."
              rows="5"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isConsultSubmitting}
            style={{ width: '100%' }}
          >
            {isConsultSubmitting ? 'Submitting...' : 'Request Consultation'}
          </button>

          {consultStatus === 'success' && (
            <p style={{ color: '#00ADB5', marginTop: '15px', fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>
              Request received. We'll respond within one business day.
            </p>
          )}
          {consultStatus === 'error' && (
            <p style={{ color: '#ff4444', marginTop: '15px', fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>
              Submission failed. Please retry or contact us directly.
            </p>
          )}

        </form>
      </GenericModal>

    </div>
  );
};

export default App;
